import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Alert, Button, Spinner, Tooltip } from "flowbite-react";
import {
  loadProdutos,
  atualizarProdutoAction,
  criarProdutoAction,
} from "../../actions/produto";
import { showNotification } from "../../actions/notification";
import { getProdutosPaginado } from "../../api/produto";
import ModalProduto from "./ModalProduto";
import ModalFotos from "../Fotos/ModalFotos";
import TabelaProdutos from "./TabelaProdutos";
import FiltroProdutos from "./FiltroProdutos";
import { FaPlus } from "react-icons/fa";
import { hasPlanAccess } from "../../utils/subscription";

const LIMIT = 30;

const Produtos = () => {
  const dispatch = useDispatch();
  const { loja, profile } = useSelector((state) => state.user);

  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modoCriacao, setModoCriacao] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [statusFiltro, setStatusFiltro] = useState("ativos");
  const [modalFotosOpen, setModalFotosOpen] = useState(false);
  const [salvandoProduto, setSalvandoProduto] = useState(false);

  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const podeEditar = hasPlanAccess(profile);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortOrder]);

  const carregarPaginaProdutos = useCallback(async () => {
    if (!loja?.id) return;

    setLoading(true);
    try {
      const { data } = await getProdutosPaginado(loja.id, {
        page: currentPage,
        limit: LIMIT,
        search: debouncedSearch,
        sortOrder,
        status: statusFiltro,
      });

      setProdutos(data?.items || []);
      setTotalPages(data?.totalPages || 1);
      setTotal(data?.total || 0);
    } catch (error) {
      dispatch(
        showNotification({
          message: error?.message || "Erro ao carregar produtos.",
          color: "failure",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, dispatch, loja?.id, sortOrder, statusFiltro]);

  useEffect(() => {
    carregarPaginaProdutos();
  }, [carregarPaginaProdutos]);

  const handleEdit = (produto) => {
    setProdutoSelecionado({
      ...produto,
      tags: produto.tags?.map((tag) => tag.nome) || [],
    });
    setModoCriacao(false);
    setModalOpen(true);
  };

  const handleNew = () => {
    setProdutoSelecionado(null);
    setModoCriacao(true);
    setModalOpen(true);
  };

  const handleDuplicate = (produto) => {
    const { id, createdAt, updatedAt, fotos, ...produtoSemId } = produto;

    setProdutoSelecionado({
      ...produtoSemId,
      fotos: [],
      tags:
        produto.tags?.map((tag) =>
          typeof tag === "object" ? tag.nome : tag
        ) || [],
    });

    setModoCriacao(true);
    setModalOpen(true);
  };

  const recarregarListasAposMutacao = async (irParaPrimeiraPagina = false) => {
    dispatch(loadProdutos(loja.id));

    if (irParaPrimeiraPagina && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    await carregarPaginaProdutos();
  };

  const handleSave = async (produtoEditado) => {
    if (!loja?.id) return;
    if (!podeEditar) {
      dispatch(
        showNotification({
          message:
            "Seu plano esta inativo. Reative em /app/planos para gravar alteracoes.",
          color: "warning",
        })
      );
      return;
    }

    const produtoParaSalvar = {
      ...produtoEditado,
      valor: produtoEditado.valor ? String(produtoEditado.valor).replace(",", ".") : null,
      valor_promocional: produtoEditado.valor_promocional
        ? String(produtoEditado.valor_promocional).replace(",", ".")
        : null,
      desconto: produtoEditado.desconto
        ? parseInt(produtoEditado.desconto, 10)
        : null,
      tamanhos_disponiveis: produtoEditado.tamanhos_disponiveis || null,
      estampa_cor: produtoEditado.estampa_cor || null,
      maximo_parcelas: produtoEditado.maximo_parcelas
        ? parseInt(produtoEditado.maximo_parcelas, 10)
        : null,
      destaque_ate: produtoEditado.destaque_ate
        ? new Date(produtoEditado.destaque_ate).toISOString()
        : null,
      ativo: produtoEditado.ativo !== false,
      tags: (produtoEditado.tags || []).map((tag) =>
        typeof tag === "object" ? tag.nome : tag
      ),
    };

    try {
      setSalvandoProduto(true);
      if (modoCriacao) {
        const res = await dispatch(
          criarProdutoAction({ ...produtoParaSalvar, lojaId: loja.id })
        );

        if (res) {
          setProdutoSelecionado(res);
          setModoCriacao(false);
        }

        await recarregarListasAposMutacao(true);
        return;
      }

      if (!produtoEditado.id) return;

      await dispatch(
        atualizarProdutoAction(produtoEditado.id, loja.id, produtoParaSalvar)
      );
      await recarregarListasAposMutacao(false);
      setModalOpen(false);
    } catch {
      dispatch(
        showNotification({
          message: modoCriacao
            ? "Erro ao criar o produto."
            : "Erro ao salvar o produto.",
          color: "failure",
        })
      );
    } finally {
      setSalvandoProduto(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleSort = (order) => {
    setSortOrder(order);
  };

  const handleStatusChange = (status) => {
    setStatusFiltro(status);
    setCurrentPage(1);
  };

  const resumoPaginacao = useMemo(() => {
    if (total === 0) return "0 resultados";

    const inicio = (currentPage - 1) * LIMIT + 1;
    const fim = Math.min(currentPage * LIMIT, total);
    return `${inicio}-${fim} de ${total} produtos`;
  }, [currentPage, total]);

  return (
    <div className="overflow-x-auto">
      <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mt-8 lg:mt-2 mb-4 select-none cursor-default">
        Produtos
      </h2>

      {!podeEditar ? (
        <div className="mb-4">
          <Alert color="warning">
            Seu plano esta inativo. Voce pode visualizar os produtos, mas
            nao pode criar ou editar ate reativar em <b>/app/planos</b>.
          </Alert>
        </div>
      ) : null}

      <FiltroProdutos
        onSearch={handleSearch}
        onSort={handleSort}
        status={statusFiltro}
        onStatusChange={handleStatusChange}
      />

      <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
        <span>{resumoPaginacao}</span>
        <span>Pagina {currentPage} de {totalPages}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="xl" />
        </div>
      ) : (
        <TabelaProdutos
          produtos={produtos}
          onProdutoClick={handleEdit}
          mostrarStatus={statusFiltro !== "ativos"}
        />
      )}

      <div className="flex justify-center gap-2 mt-6">
        <Button
          color="light"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage <= 1 || loading}
        >
          Anterior
        </Button>
        <Button
          color="light"
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage >= totalPages || loading}
        >
          Proxima
        </Button>
      </div>

      <ModalProduto
        produto={produtoSelecionado}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onDuplicate={handleDuplicate}
        canEdit={podeEditar}
        isSaving={salvandoProduto}
        onFotosChanged={carregarPaginaProdutos}
      />

      <ModalFotos
        produto={produtoSelecionado}
        isOpen={modalFotosOpen}
        onClose={() => setModalFotosOpen(false)}
      />

      <div className="fixed bottom-6 right-6">
        <Tooltip
          content={
            podeEditar
              ? "Cadastrar novo produto"
              : "Ative um plano para cadastrar produtos"
          }
          placement="left"
        >
          <Button
            gradientDuoTone="purpleToPink"
            className={`rounded-full py-6 px-4 shadow-lg ${
              podeEditar ? "" : "cursor-not-allowed opacity-50"
            }`}
            onClick={podeEditar ? handleNew : undefined}
            disabled={!podeEditar}
          >
            <FaPlus className="text-white w-6 h-6" />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default Produtos;
